import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
    Activity, AlertTriangle, Bug, Info, AlertCircle,
    Download, Clock,
} from 'lucide-react';
import { cardAdminApi, CardLog, LogLevel, LogAction } from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

const LEVEL_COLOR: Record<LogLevel, string> = {
    info: 'bg-blue-50 text-blue-600 border-blue-200',
    warn: 'bg-amber-50 text-amber-600 border-amber-200',
    error: 'bg-red-50 text-red-600 border-red-200',
    debug: 'bg-slate-50 text-slate-500 border-slate-200',
};

const LEVEL_ICON: Record<LogLevel, React.ReactNode> = {
    info: <Info className="h-3.5 w-3.5" />,
    warn: <AlertTriangle className="h-3.5 w-3.5" />,
    error: <AlertCircle className="h-3.5 w-3.5" />,
    debug: <Bug className="h-3.5 w-3.5" />,
};

const LEVEL_FILTERS: { key: LogLevel | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout' },
    { key: 'error', label: 'Erè' },
    { key: 'warn', label: 'Avètisman' },
    { key: 'info', label: 'Info' },
    { key: 'debug', label: 'Debug' },
];

const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function LogsPage() {
    const { toast } = useToast();

    const [logs, setLogs] = useState<CardLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [exportLoading, setExportLoading] = useState(false);
    const LIMIT = 50;

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            const res = await cardAdminApi.listLogs({
                page, limit: LIMIT,
                level: levelFilter,
                search: searchTerm || undefined,
            });
            setLogs(Array.isArray(res?.results) ? res.results : []);
            setTotal(res?.total ?? 0);
        } catch {
            setLogs([]);
            toast({ title: 'Erè', description: 'Pa kapab chaje logs yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, levelFilter, searchTerm]);

    useEffect(() => { loadLogs(); }, [loadLogs]);
    useEffect(() => { setPage(1); }, [levelFilter, searchTerm]);

    const handleExport = async () => {
        try {
            setExportLoading(true);
            const blob = await cardAdminApi.exportLogs({ format: 'csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bousanm-card-logs-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast({ title: 'Erè', description: 'Echèk ekspòtasyon', type: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Logs</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Audit Logs — Module Kat</h1>
                            <p className="text-slate-500 text-sm mt-1">Tout aksyon, webhook ak apèl API HyperPay.</p>
                        </div>
                        <div className="flex items-center gap-2 self-start sm:self-auto">
                            <Button variant="outline" size="sm" className="gap-2 border-slate-200" onClick={handleExport} disabled={exportLoading}>
                                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                Ekspòte CSV
                            </Button>
                            <Button variant="outline" size="sm" className="gap-2 border-slate-200" onClick={loadLogs}>
                                <RefreshCw className="h-4 w-4" /> Aktyalize
                            </Button>
                        </div>
                    </div>

                    {/* Level Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
                        {LEVEL_FILTERS.map(({ key, label }) => (
                            <button key={key} onClick={() => setLevelFilter(key as LogLevel | 'all')}
                                className={cn('flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    levelFilter === key
                                        ? key === 'error' ? 'bg-red-600 text-white shadow-md'
                                            : key === 'warn' ? 'bg-amber-500 text-white shadow-md'
                                                : 'bg-primary-600 text-white shadow-md'
                                        : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >{label}</button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input placeholder="Chèche pa mesaj, entite, oswa kliyan..." className="pl-10 h-10 border-slate-200 rounded-xl" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>

                    {/* Log list */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-24">Nivo</th>
                                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Aksyon</th>
                                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Mesaj</th>
                                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Entite</th>
                                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-40">Dat</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr><td colSpan={5} className="px-6 py-16 text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                            <p className="text-slate-500 mt-3 text-sm">Ap chaje logs yo...</p>
                                        </td></tr>
                                    ) : logs.length === 0 ? (
                                        <tr><td colSpan={5} className="px-6 py-16 text-center">
                                            <Activity className="h-10 w-10 text-slate-200 mx-auto" />
                                            <p className="text-slate-500 mt-3 font-medium">Okenn log jwenn</p>
                                        </td></tr>
                                    ) : logs.map((log) => (
                                        <React.Fragment key={log.id}>
                                            <tr
                                                className={cn(
                                                    'hover:bg-slate-50/50 transition-colors cursor-pointer',
                                                    log.level === 'error' ? 'bg-red-50/30' : ''
                                                )}
                                                onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                                            >
                                                <td className="px-4 py-3">
                                                    <Badge variant="outline" className={cn('text-xs gap-1', LEVEL_COLOR[log.level])}>
                                                        {LEVEL_ICON[log.level]}
                                                        {log.level.toUpperCase()}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs font-mono font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                                                        {log.action}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-sm text-slate-800 max-w-xs truncate">{log.message}</p>
                                                    {log.adminName && (
                                                        <p className="text-xs text-slate-400 mt-0.5">Admin: {log.adminName}</p>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {log.entityType && (
                                                        <p className="text-xs font-mono text-slate-500">
                                                            {log.entityType}: {log.entityId?.slice(0, 12)}…
                                                        </p>
                                                    )}
                                                    {log.clientId && (
                                                        <p className="text-xs text-slate-400 mt-0.5 font-mono">
                                                            {log.clientId.slice(0, 16)}…
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                        <Clock className="h-3 w-3" />
                                                        {formatDate(log.createdAt)}
                                                    </div>
                                                    {log.ipAddress && (
                                                        <p className="text-xs font-mono text-slate-300 mt-0.5">{log.ipAddress}</p>
                                                    )}
                                                </td>
                                            </tr>
                                            {/* Expanded meta row */}
                                            {expanded === log.id && log.meta && (
                                                <tr>
                                                    <td colSpan={5} className="px-6 py-3 bg-slate-900">
                                                        <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap overflow-x-auto max-h-48">
                                                            {JSON.stringify(log.meta, null, 2)}
                                                        </pre>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-sm text-slate-500">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} sou {total} logs</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                                    <span className="text-sm font-medium">{page}</span>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
